import React, { useState, useEffect, useRef } from 'react';
import WebTerminal from './WebTerminal';
import { Terminal as TerminalIcon, Loader2, ServerCrash, Power, ShieldCheck, Key, ChevronRight } from 'lucide-react';
import io from 'socket.io-client';

import SharedSSHUnlock from './SharedSSHUnlock';

const SSHTab = ({ tenant, isVisible, sshSessionId, socket, sshStatus, sshError, onConnect, onDisconnect, isLeftPanelOpen, onToggleLeftPanel }) => {

  const sendCommand = (cmd) => {
    if (socket && sshSessionId) {
      socket.emit('ssh:input', { sessionId: sshSessionId, data: cmd });
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
        <div className="flex items-center gap-3">
          {!isLeftPanelOpen && (
            <button
              onClick={onToggleLeftPanel}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
              title="Déplier Fiche Instance"
            >
              <ChevronRight size={18} />
            </button>
          )}
          <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-100">
            <TerminalIcon size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Terminal SSH</h3>
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-mono">
                {tenant?.vpsId || tenant?.schoolName}
              </span>
            </div>
            <div className="text-xs font-medium flex items-center gap-2 mt-0.5">
              {sshStatus === 'connected' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"></span>
                  <span className="text-emerald-600 font-semibold">Connecté</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-600 font-mono flex items-center gap-1"><Key size={12} /> ubuntu</span>
                  {sshSessionId && (
                    <>
                      <span className="text-slate-300">|</span>
                      <span className="text-slate-500 font-mono">ID: {sshSessionId.substring(0, 8)}</span>
                    </>
                  )}
                </>
              )}
              {sshStatus === 'connecting' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  <span className="text-amber-600 font-semibold">Connexion...</span>
                </>
              )}
              {sshStatus === 'idle' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                  <span className="text-slate-500">Non connecté</span>
                </>
              )}
              {sshStatus === 'error' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                  <span className="text-rose-600 font-semibold">Déconnecté</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {sshStatus === 'connected' && (
            <button
              onClick={() => {
                if (onDisconnect) {
                  onDisconnect();
                } else {
                  if (tenant?.id) sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
                  if (socket && sshSessionId) {
                    socket.emit('ssh:disconnect', { sessionId: sshSessionId });
                  }
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-all shadow-xs"
              title="Déconnecter le SSH"
            >
              <Power size={14} />
              <span>Déconnecter</span>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-6 overflow-hidden relative">
        {sshStatus !== 'connected' ? (
          <SharedSSHUnlock 
            tenant={tenant} 
            status={sshStatus} 
            errorMessage={sshError} 
            onConnect={onConnect} 
            title="Secure SSH Access"
            description="Establish a secure connection to manage this instance directly from your browser."
          />
        ) : (
          <div className="flex-1 w-full h-full bg-[#1e1e1e] rounded-xl overflow-hidden shadow-xl ring-1 ring-white/10 flex flex-col relative z-10">
            {/* Macros Bar */}
            <div className="flex gap-2 p-3 bg-[#252526] border-b border-[#3c3c3c] overflow-x-auto shrink-0">
              <span className="text-[#858585] text-xs font-semibold mr-2 flex items-center uppercase tracking-widest">Macros:</span>
              <button onClick={() => sendCommand(`docker exec -it mongo_${tenant?.DbName} mongosh -u superadmin -p "${tenant?.mongoPassword}" --authenticationDatabase admin\n`)} className="px-3 py-1.5 bg-[#0e639c] hover:bg-[#1177bb] text-white text-xs rounded transition-colors whitespace-nowrap">🗄️ Console Mongo</button>
              <button onClick={() => sendCommand(`docker stats backend_${tenant?.DbName} mongo_${tenant?.DbName} jitsi-jvb_${tenant?.DbName} --no-stream --format "{{.MemUsage}} | {{.CPUPerc}}"\n`)} className="px-3 py-1.5 bg-[#4d4d4d] hover:bg-[#5a5a5a] text-white text-xs rounded transition-colors whitespace-nowrap">📊 Stats Docker</button>
              <button onClick={() => sendCommand(`docker restart backend_${tenant?.DbName}\n`)} className="px-3 py-1.5 bg-[#d16969] hover:bg-[#e06c6c] text-white text-xs rounded transition-colors whitespace-nowrap">🔄 Restart API</button>
              <button onClick={() => sendCommand(`clear\n`)} className="px-3 py-1.5 bg-[#4d4d4d] hover:bg-[#5a5a5a] text-white text-xs rounded transition-colors whitespace-nowrap">🧹 Clear</button>
            </div>
            {sshSessionId && <WebTerminal socket={socket} sessionId={sshSessionId} dbName={tenant?.DbName} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SSHTab);
